import React, { useState, useEffect, useRef } from 'react';
import { ArrowsPointingOutIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { refreshScreenshotUrl } from '../../utils/screenshotUtils';
import styles from '../../styles/automation/screenshotImage.module.scss';

const ScreenshotImage = ({
    src,
    rawPath = null,
    alt = 'Screenshot',
    onClick,
    size = 'medium',
    showOverlay = true,
    clickable = true,
    borderStyle = 'solid',
}) => {
    const [imageState, setImageState] = useState('loading');
    const [isHovered, setIsHovered] = useState(false);
    const [displaySrc, setDisplaySrc] = useState(src);
    const retriedRef = useRef(false);

    useEffect(() => {
        setDisplaySrc(src);
        setImageState('loading');
        retriedRef.current = false;
    }, [src]);

    const handleImageLoad = () => {
        setImageState('loaded');
    };

    const handleImageError = async () => {
        const retrySource = rawPath || src;
        if (!retriedRef.current && retrySource) {
            retriedRef.current = true;
            const { url } = await refreshScreenshotUrl(retrySource);
            if (url) {
                setDisplaySrc(url);
                setImageState('loading');
                return;
            }
        }
        setImageState('error');
    };

    const handleClick = () => {
        if (clickable && imageState === 'loaded' && onClick) {
            onClick({ imageUrl: displaySrc, fileName: alt, rawPath: rawPath || src });
        }
    };

    const renderImageContent = () => {
        switch (imageState) {
            case 'loading':
                return (
                    <div className={styles.imageLoadingState}>
                        <div className={styles.loadingSpinner}></div>
                        <span className={styles.loadingText}>Loading...</span>
                    </div>
                );
            case 'error':
                return (
                    <div className={styles.imageErrorState}>
                        <PhotoIcon className={styles.errorIcon} />
                        <span className={styles.errorText}>Image not available</span>
                    </div>
                );
            case 'loaded':
                return (
                    <img
                        src={displaySrc}
                        alt={alt}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        className={styles.screenshotImage}
                    />
                );
            default:
                return null;
        }
    };

    const containerClasses = [
        styles.screenshotContainer,
        styles[`size-${size}`],
        styles[`border-${borderStyle}`],
        isHovered ? styles.hovered : '',
        clickable && imageState === 'loaded' ? styles.clickable : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={containerClasses}
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {imageState !== 'loaded' && displaySrc && (
                <img
                    src={displaySrc}
                    alt={alt}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: 'none' }}
                />
            )}
            {renderImageContent()}
            {showOverlay && imageState === 'loaded' && clickable && (
                <div className={styles.eyeIconOverlay}>
                    <ArrowsPointingOutIcon className={styles.eyeIcon} />
                </div>
            )}
        </div>
    );
};

export default ScreenshotImage;
